import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import type { PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useLocation } from 'react-router';

import type { RootLoaderType } from '../../data-access';
import {
  useGetCart,
  useGetJwt,
  useGetKiosk,
  useGetWhoami,
  useKioskUser,
} from '../../data-access';
import { useTypedRouteLoaderData } from '../../hooks';
import type { IdleContext as IdleContextType } from '../../types';
import {
  resetSignInPrompt,
  trackSessionTimedOut,
  trackTimeoutDialogDisplayed,
} from '../../util';
import { useEnsureContextWithinProvider } from './hooks/useEnsureContextWithinProvider';
import { useLogoutUser } from './hooks/useLogoutUser';
import { useResetKiosk } from './hooks/useResetKiosk';

const IdleContext = createContext<IdleContextType | null>(null);

const NO_IDLE_PATHS = [
  /^\/stores\/\d+\/landing$/,
  /^\/stores\/\d+\/cart\/link-bank$/,
  // Any pathname containing /mobile
  /\/mobile\//,
  // Can contain any segment between stores and login, but must end in /login
  /^\/stores\/\d+\/(?:.*\/)?login(?:\/.*)?$/,
];

export const IdleProvider = ({ children }: PropsWithChildren) => {
  const [timerDisabled, setTimerDisabled] = useState(false);
  const {
    kiosk: initialKiosk,
    kioskId,
    storeId,
    whoami: initialWhoami,
  } = useTypedRouteLoaderData<RootLoaderType>('root');
  const { data: kioskUser } = useKioskUser();
  const { data: whoami, isSuccess: isWhoamiSuccess } = useGetWhoami({
    options: { initialData: initialWhoami },
  });
  const { data: kiosk } = useGetKiosk({
    kioskId,
    initialData: initialKiosk,
  });
  const { data: jwt } = useGetJwt();

  const { pathname } = useLocation();

  const isQrCheckout = useMemo(
    () => pathname.includes('/reservation/qr'),
    [pathname]
  );

  const { data: cart } = useGetCart();
  const { resetKiosk } = useResetKiosk();
  const { logoutAndReset } = useLogoutUser();

  const kioskDeviceExpirationRedirectEnabled = useFlag(
    FLAGS.c3KioskDeviceExpirationRedirectEnabled
  );
  const hadJwt = useRef(false);

  useEffect(() => {
    // redirect to kiosk login page when kiosk session expires
    if (jwt) {
      hadJwt.current = true;
    } else if (hadJwt.current) {
      hadJwt.current = false;

      if (kioskDeviceExpirationRedirectEnabled) {
        resetKiosk();
      }
    }
  }, [jwt, kioskDeviceExpirationRedirectEnabled, resetKiosk, storeId]);

  const isAuthenticated = isWhoamiSuccess && !!jwt && !!whoami.id;

  const noIdlePaths = useMemo(
    () => NO_IDLE_PATHS.some((path) => path.test(pathname)),
    [pathname]
  );

  const isTimerDisabled = noIdlePaths || !isAuthenticated || timerDisabled;

  /**
   * These two states *could* be derived from the result of useIdleTimer, but
   * states are more convenient to React-ively drive UI changes when presenting
   * the 'Are you still there?' prompt.
   */
  const [hasActivated, setHasActivated] = useState(false);
  const [isPromptShown, setIsPromptShown] = useState(false);

  const timeout = useMemo(
    () =>
      /**
       * Check `KioskSettingsModal` ln259 in the business-admin lib for more info.
       * These two fields are set in the database from a single, user input value.
       * Using both props would split the idle behavior (prompt, then reset) into equal durations.
       * Instead, we combine these into a single value & use a static timeout for the prompt.
       */
      (Number(kiosk?.inactivity_timeout_ms) || 0) +
      (Number(kiosk?.refresh_to_landing_timeout_ms) || 100 * 1000),
    [kiosk?.inactivity_timeout_ms, kiosk?.refresh_to_landing_timeout_ms]
  );

  const [idleValue, promptTiming] = useMemo(() => {
    const promptTime = 15 * 1000;
    const idleTimeout = kioskUser ? 10 * 1000 : timeout;
    const totalTimeout = idleTimeout + promptTime;

    return [totalTimeout, promptTime];
  }, [kioskUser, timeout]);

  const reset = useCallback(() => {
    if (isQrCheckout) {
      resetKiosk({ shouldDeleteCart: false });
    } else {
      logoutAndReset();
    }

    resetSignInPrompt();
    setIsPromptShown(false);
    setHasActivated(false);
  }, [isQrCheckout, logoutAndReset, resetKiosk]);

  const onIdle = useCallback(() => {
    trackSessionTimedOut({
      cart,
      timeoutMs: timeout,
    });
    reset();
  }, [cart, reset, timeout]);

  const onPrompt = useCallback(() => {
    trackTimeoutDialogDisplayed({
      cart: cart,
      timeoutMs: timeout,
    });
    setIsPromptShown(true);
  }, [cart, timeout]);

  const { activate, getRemainingTime, isIdle } = useIdleTimer({
    disabled: isTimerDisabled,
    onIdle,
    onPrompt,
    promptBeforeIdle: promptTiming,
    startOnMount: true,
    startManually: !!kiosk?.skip_landing_page,
    throttle: 500,
    timeout: idleValue,
  });

  const restartTimer = useCallback(() => {
    activate();
    setIsPromptShown(false);
    setHasActivated(true);
  }, [activate]);

  const contextValue = useMemo(
    (): IdleContextType => ({
      activate: restartTimer,
      hasActivated,
      isPromptVisible: isPromptShown,
      reset,
      setTimerDisabled,
      timeoutMs: timeout,
      /**
       * react-idle-timer attributes below
       */
      getRemainingTime,
      isIdle,
    }),
    [
      getRemainingTime,
      hasActivated,
      isIdle,
      isPromptShown,
      reset,
      restartTimer,
      timeout,
    ]
  );

  return (
    <IdleContext.Provider value={contextValue}>{children}</IdleContext.Provider>
  );
};

export const useIdleContext = () =>
  useEnsureContextWithinProvider(IdleContext, 'Idle');
